/**
 * V11.19 — Console admin : rotation manuelle des "Athlètes à la UNE".
 *
 * Permet à un admin/COPF de :
 *   • Épingler ("pin") un athlète en haut de la UNE pour une semaine
 *   • Reléguer ("relegate") un athlète (exclu de la UNE) pour une semaine
 *   • Voir et supprimer les entrées de rotation existantes
 *
 * Route : /admin/featured-rotation
 * Toutes les actions sont auditées côté backend.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, Loader2, ArrowLeft, Search, User, Pin, X, RotateCcw, ArrowDownCircle, ShieldAlert } from "lucide-react";

function getIsoWeek(d = new Date()) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNum = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return { year: date.getUTCFullYear(), week: weekNum };
}

export default function AdminFeaturedRotationPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const now = getIsoWeek();
  const [year, setYear] = useState(now.year);
  const [week, setWeek] = useState(now.week);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [note, setNote] = useState("");

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/hero/rotation", { params: { iso_year: year, iso_week: week } });
      setEntries(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Rotation: " });
    } finally { setLoading(false); }
  }, [year, week]);

  useEffect(() => {
    if (!authLoading && !isAdmin) { navigate("/admin"); return; }
    if (isAdmin) loadEntries();
  }, [authLoading, isAdmin, navigate, loadEntries]);

  // Charger la preview du classement automatique comme liste de candidats
  const loadPreview = useCallback(async () => {
    setSearching(true);
    try {
      const { data } = await api.get("/admin/hero/preview", { params: { limit: 20 } });
      setCandidates(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Preview: " });
    } finally { setSearching(false); }
  }, []);

  useEffect(() => { if (isAdmin) loadPreview(); }, [isAdmin, loadPreview]);

  const runSearch = async () => {
    if (!query.trim()) { loadPreview(); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/athletes", { params: { q: query.trim(), limit: 20, published: true } });
      setCandidates(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Recherche: " });
    } finally { setSearching(false); }
  };

  const applyAction = async (athlete_id, action) => {
    try {
      await api.post("/admin/hero/rotation", { athlete_id, action, iso_year: year, iso_week: week, note: note || null });
      toast.success(action === "pin" ? "Athlète épinglé cette semaine." : "Athlète relégué cette semaine.");
      setNote("");
      await loadEntries();
    } catch (e) { showError(e, { prefix: "Rotation: " }); }
  };

  const removeEntry = async (id) => {
    try {
      await api.delete(`/admin/hero/rotation/${id}`);
      toast.success("Entrée supprimée.");
      await loadEntries();
    } catch (e) { showError(e, { prefix: "Rotation: " }); }
  };

  if (authLoading) return <div className="max-w-5xl mx-auto p-8"><Skeleton className="h-8 w-1/3" /></div>;
  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Card className="p-6 border-destructive/50 bg-destructive/5">
          <div className="flex items-center gap-2 mb-2"><ShieldAlert className="h-5 w-5 text-destructive" /><h1 className="font-display font-bold">Accès admin requis</h1></div>
          <p className="text-sm">Cette console est réservée aux comptes admin / COPF.</p>
        </Card>
      </div>
    );
  }

  const pinned = entries.filter((e) => e.action === "pin");
  const relegated = entries.filter((e) => e.action === "relegate");

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="admin-featured-rotation-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => navigate("/admin")}><ArrowLeft className="h-4 w-4 mr-1" />Admin</Button>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <Star className="h-5 w-5 text-[hsl(var(--aito-gold))]" />
          Rotation "Athlètes à la UNE"
        </h1>
      </div>
      <p className="text-sm text-muted-foreground max-w-3xl">
        Pilotage hebdomadaire manuel : <strong>Épingle</strong> un athlète pour qu'il apparaisse en tête cette semaine,
        <strong> relègue</strong>-en un pour qu'il soit temporairement retiré de la UNE. La rotation automatique reprend
        la semaine suivante sans action de ta part.
      </p>

      {/* Sélecteur semaine */}
      <Card className="p-4 flex items-end gap-3 flex-wrap" data-testid="admin-rotation-week-picker">
        <div>
          <Label className="text-xs">Année ISO</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-28" data-testid="admin-rotation-year" />
        </div>
        <div>
          <Label className="text-xs">Semaine ISO</Label>
          <Input type="number" min={1} max={53} value={week} onChange={(e) => setWeek(Number(e.target.value))} className="w-24" data-testid="admin-rotation-week" />
        </div>
        <Button size="sm" onClick={loadEntries} data-testid="admin-rotation-reload">
          <RotateCcw className="h-4 w-4 mr-1" /> Charger
        </Button>
        <div className="text-xs text-muted-foreground ml-auto">
          Semaine courante : {now.year}-W{String(now.week).padStart(2, "0")}
        </div>
      </Card>

      {/* Entrées actives */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4" data-testid="admin-rotation-pinned">
          <h2 className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5">
            <Pin className="h-4 w-4 text-[hsl(var(--aito-gold))]" /> Épinglés cette semaine ({pinned.length})
          </h2>
          {loading ? <Skeleton className="h-24 w-full" /> : pinned.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Aucun athlète épinglé — la rotation automatique s'applique.</p>
          ) : (
            <ul className="space-y-2">
              {pinned.map((e) => (
                <li key={e.id} className="flex items-center gap-2 p-2 rounded border bg-[hsl(var(--aito-gold))]/5">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.athlete_name}</div>
                    {e.note && <div className="text-[11px] text-muted-foreground truncate">{e.note}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeEntry(e.id)} data-testid={`admin-rotation-unpin-${e.athlete_id}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-4" data-testid="admin-rotation-relegated">
          <h2 className="font-display font-semibold text-sm mb-3 flex items-center gap-1.5">
            <ArrowDownCircle className="h-4 w-4 text-muted-foreground" /> Relégués cette semaine ({relegated.length})
          </h2>
          {loading ? <Skeleton className="h-24 w-full" /> : relegated.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Aucun athlète relégué.</p>
          ) : (
            <ul className="space-y-2">
              {relegated.map((e) => (
                <li key={e.id} className="flex items-center gap-2 p-2 rounded border bg-muted">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.athlete_name}</div>
                    {e.note && <div className="text-[11px] text-muted-foreground truncate">{e.note}</div>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeEntry(e.id)} data-testid={`admin-rotation-restore-${e.athlete_id}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Candidats + actions */}
      <Card className="p-4" data-testid="admin-rotation-candidates">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h2 className="font-display font-semibold text-sm flex items-center gap-1.5"><Search className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /> Candidats</h2>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Input placeholder="Rechercher un athlète…" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} data-testid="admin-rotation-search-input" />
          </div>
          <Button size="sm" onClick={runSearch} disabled={searching} data-testid="admin-rotation-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setQuery(""); loadPreview(); }} data-testid="admin-rotation-reset-btn">
            Top classement auto
          </Button>
        </div>
        <div className="mb-3">
          <Label className="text-xs">Note interne (optionnelle)</Label>
          <Input placeholder="Ex : victoire au championnat du monde" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-rotation-note" />
        </div>
        {searching ? <Skeleton className="h-40 w-full" /> : candidates.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucun athlète trouvé.</p>
        ) : (
          <ul className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {candidates.map((a) => (
              <li key={a.id} className="flex items-center gap-2 p-2 rounded border hover:bg-muted/50" data-testid={`admin-rotation-candidate-${a.id}`}>
                <img src={resolveAthletePhoto(a)} alt="" className={`h-8 w-8 rounded-full ${a.photo_url ? "object-cover" : "object-cover bg-white"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.full_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {a.main_discipline || (a.disciplines || [])[0] || "—"}
                    {a.total != null && <> · score {a.total}</>}
                  </div>
                </div>
                {(a.missing || []).length > 0 && (
                  <Badge variant="outline" className="text-[10px]">manque : {(a.missing || []).join(", ")}</Badge>
                )}
                <Button size="sm" onClick={() => applyAction(a.id, "pin")} className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]/80" data-testid={`admin-rotation-pin-${a.id}`}>
                  <Pin className="h-3.5 w-3.5 mr-1" /> Épingler
                </Button>
                <Button size="sm" variant="outline" onClick={() => applyAction(a.id, "relegate")} data-testid={`admin-rotation-relegate-${a.id}`}>
                  <ArrowDownCircle className="h-3.5 w-3.5 mr-1" /> Reléguer
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
